import clsx from 'clsx';
import { forwardRef, type ReactNode, type ReactElement } from 'react';
import { isElement } from 'react-is';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { _canReceiveDefaultTextFormat } from '@dynatrace/strato-components/core';
import { Text } from '@dynatrace/strato-components/typography';

import { detailsSlotCss } from '../EmptyState.sty.js';

//#region Typings
/**
 * @public
 */
export interface EmptyStateDetailsProps
  extends WithChildren,
    StylingProps,
    DataTestId {}
//#endregion Typings

/**
 * Typeguard function that will check if a ReactNode is a EmptyState.Details
 * @internal
 */
export function isEmptyStateDetails(
  child: ReactNode,
): child is ReactElement<EmptyStateDetailsProps> {
  return isElement(child) && child.type === Details;
}

/**
 * The Details slot of the EmptyState component.
 * @public
 */
export const Details = /* @__PURE__ */ forwardRef<
  HTMLParagraphElement,
  EmptyStateDetailsProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  } = props;

  const attrs = {
    ref: forwardedRef,
    className: clsx([detailsSlotCss, consumerClassName]),
    style: consumerStyle,
    'data-testid': dataTestId,
  };

  return _canReceiveDefaultTextFormat(children) ? (
    <Text textStyle="base" as="p" {...attrs}>
      {children}
    </Text>
  ) : (
    <div {...attrs}>{children}</div>
  );
});

(Details as typeof Details & { displayName: string }).displayName =
  'EmptyState.Details';
